import type {RefObject} from 'react'
import {useCallback, useMemo, useState} from 'react'

import {useMarkersDialogContext} from '../contexts/MarkersDialogContext'

type ActionBarDialogs = {
  // Context menus
  isContextMenuOpen: boolean
  openContextMenu: () => void
  openContextMenuFromActionBar: () => void
  closeContextMenu: () => void
  toggleContextMenu: () => void
  toggleContextMenuFromActionBar: () => void

  closeMarkerListDialog: () => void

  // Misc
  returnFocusRef: RefObject<HTMLElement>
  anyMenuOpen: boolean
}

export function useActionBarDialogs({
  cellId,
  actionBarRef,
}: {
  cellId?: string
  actionBarRef: RefObject<HTMLElement>
}): ActionBarDialogs {
  const [returnFocusRef, setReturnFocusRef] = useState<RefObject<HTMLElement>>(actionBarRef)
  const {openDialog, closeDialog, openContextMenuCell, anyMenuOpen} = useMarkersDialogContext()

  // Context menus
  const isContextMenuOpen = useMemo(() => openContextMenuCell === cellId, [cellId, openContextMenuCell])

  const openContextMenu = useCallback(() => {
    openDialog('context-menu', cellId)
  }, [cellId, openDialog])

  const openContextMenuFromActionBar = useCallback(() => {
    setReturnFocusRef(actionBarRef)
    openContextMenu()
  }, [actionBarRef, openContextMenu])

  const closeContextMenu = useCallback(() => {
    closeDialog('context-menu')
  }, [closeDialog])

  const toggleContextMenu = useCallback(() => {
    if (isContextMenuOpen) {
      closeContextMenu()
    } else {
      openContextMenu()
    }
  }, [closeContextMenu, isContextMenuOpen, openContextMenu])

  const toggleContextMenuFromActionBar = useCallback(() => {
    setReturnFocusRef(actionBarRef)
    toggleContextMenu()
  }, [actionBarRef, toggleContextMenu])

  const closeMarkerListDialog = useCallback(() => {
    closeDialog('marker-list')
  }, [closeDialog])

  return {
    // Context menus
    isContextMenuOpen,
    openContextMenu,
    openContextMenuFromActionBar,
    closeContextMenu,
    toggleContextMenu,
    toggleContextMenuFromActionBar,

    closeMarkerListDialog,
    returnFocusRef,
    anyMenuOpen,
  }
}
